import { Fragment, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useProjectForPrint } from "@/hooks/use-project-for-print";
import { useProjectExtras } from "@/hooks/use-project-extras";
import { formatNOK, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { SrIcon } from "@/components/ui/SrIcon";
import ErrorBoundary from "@/components/shared/ErrorBoundary";

/**
 * VIB-675: Printbar liste over statister/eksterne på et prosjekt.
 * Brukes av regnskapsfører for å se navn, tlf, e-post, adresse, personnr,
 * beløp, prosjekt-kode, dato på papir.
 *
 * Layout er optimalisert for print via @media print-CSS i index.css:
 *  - .print-hide skjuler knapper og navigasjon ved utskrift
 *  - .print-root får ingen padding/margin slik at innholdet fyller A4
 *  - AppLayout (sidebar/header) er ikke rundt denne ruten — den er
 *    registrert utenfor AuthGate så vi rendrer den som dedikert side
 *    (analog til /skjema/:token).
 *
 * VIB-647: useProjectForPrint er nå flyttet ut til egen hook for å holde
 * sider unna direkte supabase-import.
 */

export default function StatisterPrintPage() {
  const { id } = useParams();
  const { data: project, isLoading: projLoading, error: projError } = useProjectForPrint(id);
  const { data: extras, isLoading: extrasLoading } = useProjectExtras(id);

  // Auto-trigger print så regnskapsfører ikke trenger å huske Cmd+P. Vent litt
  // til DOM er ferdig rendret. Hvis brukeren avbryter printen får de en
  // synlig side med "Skriv ut"-knapp som backup.
  useEffect(() => {
    if (!projLoading && !extrasLoading && project && extras) {
      const t = setTimeout(() => window.print(), 400);
      return () => clearTimeout(t);
    }
  }, [projLoading, extrasLoading, project, extras]);

  if (projLoading || extrasLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <SrIcon name="loader2" className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (projError || !project) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-destructive">Fant ikke prosjekt.</p>
      </div>
    );
  }

  const printDate = formatDate(new Date().toISOString());

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-white text-foreground">
        {/* Toolbar — skjult i print */}
        <div className="print-hide border-b sticky top-0 bg-white z-10">
          <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between">
            <Link
              to={`/prosjekter/${project.id}/okonomi?seksjon=statister`}
              className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
            >
              <SrIcon name="chevron-left" className="w-4 h-4" /> Tilbake til prosjekt
            </Link>
            <Button size="sm" onClick={() => window.print()}>
              <SrIcon name="file-text" className="w-3.5 h-3.5 mr-1.5" /> Skriv ut
            </Button>
          </div>
        </div>

        <div className="print-root max-w-5xl mx-auto px-8 py-8">
          <header className="mb-6">
            <h1 className="text-2xl font-semibold">Statister / Lønnet ekstern</h1>
            <div className="mt-2 text-sm text-muted-foreground flex flex-wrap gap-x-6 gap-y-1">
              <span>
                <strong className="text-foreground">Prosjekt:</strong> {project.name}
              </span>
              {project.project_code && (
                <span>
                  <strong className="text-foreground">Prosjektkode:</strong> {project.project_code}
                </span>
              )}
              <span>
                <strong className="text-foreground">Utskriftsdato:</strong> {printDate}
              </span>
              <span>
                <strong className="text-foreground">Antall:</strong> {extras?.length ?? 0}
              </span>
            </div>
          </header>

          {!extras || extras.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">
              Ingen statister registrert på dette prosjektet.
            </p>
          ) : (
            // 2-linjers layout per statist: rad 1 (navn/tlf/e-post),
            // rad 2 (adresse/personnr/kontonr/beløp/dato). Kolonnene ville blitt
            // trange på A4 om alt skulle inn på én rad.
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="border-b-2 border-foreground">
                  <th className="text-left py-2 px-2 font-semibold w-1/4">Navn</th>
                  <th className="text-left py-2 px-2 font-semibold w-1/6">Rolle</th>
                  <th className="text-left py-2 px-2 font-semibold w-1/5">Tlf</th>
                  <th className="text-left py-2 px-2 font-semibold" colSpan={2}>E-post</th>
                </tr>
                <tr className="border-b-2 border-foreground text-muted-foreground">
                  <th className="text-left py-2 px-2 font-semibold">Adresse</th>
                  <th className="text-left py-2 px-2 font-semibold">Personnr</th>
                  <th className="text-left py-2 px-2 font-semibold">Kontonr</th>
                  <th className="text-right py-2 px-2 font-semibold">Beløp</th>
                  <th className="text-left py-2 px-2 font-semibold">Dato</th>
                </tr>
              </thead>
              <tbody>
                {extras.map((ex) => (
                  <Fragment key={ex.id}>
                    <tr className="align-top">
                      <td className="pt-3 px-2 font-medium">{ex.full_name ?? ex.name ?? "—"}</td>
                      <td className="pt-3 px-2">{ex.role ?? "—"}</td>
                      <td className="pt-3 px-2">{ex.phone_number ?? "—"}</td>
                      <td className="pt-3 px-2" colSpan={2}>{ex.email ?? "—"}</td>
                    </tr>
                    <tr className="border-b border-foreground/20 align-top text-foreground/80">
                      <td className="pb-3 px-2">{ex.address ?? "—"}</td>
                      <td className="pb-3 px-2 font-mono">{ex.personal_number ?? "—"}</td>
                      <td className="pb-3 px-2 font-mono">{ex.bank_account ?? "—"}</td>
                      <td className="pb-3 px-2 text-right tabular-nums">
                        {ex.agreed_amount != null ? formatNOK(ex.agreed_amount) : "—"}
                      </td>
                      <td className="pb-3 px-2">{formatDate(ex.created_at)}</td>
                    </tr>
                  </Fragment>
                ))}
              </tbody>
            </table>
          )}

          <footer className="mt-8 text-[10px] text-muted-foreground border-t pt-3">
            Sølvrev — generert {printDate}
          </footer>
        </div>
      </div>
    </ErrorBoundary>
  );
}
